import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { formatPrice } from '@/lib/formatters';

export interface NeighborhoodStats {
  totalListings: number;
  medianPrice: number;
  minPrice: number;
  maxPrice: number;
  avgBedrooms: number;
  avgBathrooms: number;
  avgSqft: number;
  propertyTypes: Record<string, number>;
  priceRange: string;
}

export function useNeighborhoodStats(neighborhood: string | undefined, city: string) {
  return useQuery({
    queryKey: ['neighborhood-stats', neighborhood, city],
    queryFn: async (): Promise<NeighborhoodStats | null> => {
      if (!neighborhood) return null;

      const { data: properties, error } = await supabase
        .from('properties')
        .select('price, bedrooms, bathrooms, sqft, property_type')
        .eq('city', city)
        .eq('neighborhood', neighborhood)
        .eq('status', 'Active');

      if (error) throw error;
      if (!properties || properties.length === 0) return null;

      // Calculate statistics
      const prices = properties.map(p => p.price).sort((a, b) => a - b);
      const medianPrice = prices[Math.floor(prices.length / 2)];
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      const bedroomsWithValue = properties.filter(p => p.bedrooms != null);
      const avgBedrooms = bedroomsWithValue.length > 0
        ? bedroomsWithValue.reduce((sum, p) => sum + (p.bedrooms || 0), 0) / bedroomsWithValue.length
        : 0;

      const bathroomsWithValue = properties.filter(p => p.bathrooms != null);
      const avgBathrooms = bathroomsWithValue.length > 0
        ? bathroomsWithValue.reduce((sum, p) => sum + (p.bathrooms || 0), 0) / bathroomsWithValue.length
        : 0;

      const sqftWithValue = properties.filter(p => p.sqft != null);
      const avgSqft = sqftWithValue.length > 0
        ? sqftWithValue.reduce((sum, p) => sum + (p.sqft || 0), 0) / sqftWithValue.length
        : 0;

      // Count property types
      const propertyTypes: Record<string, number> = {};
      properties.forEach(p => {
        const type = p.property_type || 'Other';
        propertyTypes[type] = (propertyTypes[type] || 0) + 1;
      });

      // Format price range
      const priceRange = `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;

      return {
        totalListings: properties.length,
        medianPrice,
        minPrice,
        maxPrice,
        avgBedrooms: Math.round(avgBedrooms * 10) / 10,
        avgBathrooms: Math.round(avgBathrooms * 10) / 10,
        avgSqft: Math.round(avgSqft),
        propertyTypes,
        priceRange,
      };
    },
    enabled: !!neighborhood,
    staleTime: 5 * 60 * 1000,
  });
}
